import api, { ApiResponse } from './api';

import {
  User,
  ConfirmEmailProps,
  ForgotPasswordProps,
  ResetPasswordProps,
  SignInProps,
  SignUpProps,
  SocialSignInProps,
  SocialSignUpProps,
  UpdateUserProps,
  UploadUserImageProps,
  ConfirmWhatsappProps,
  SpoofedInUserProps,
} from 'entities/User';

interface SignInResponse {
  success: boolean;
  data: {
    user: User;
    token: string;
  };
}

export const signInService = (data: SignInProps): ApiResponse<SignInResponse> =>
  api({ method: 'POST', url: 'sessions', data });

export const socialSignInService = (
  data: SocialSignInProps
): ApiResponse<SignInResponse> =>
  api({ method: 'POST', url: 'social-auth/sign-in', data });

interface SignUpResponse {
  success: boolean;
  data: {
    user: User;
    token: string;
  };
}

export const signUpService = (data: SignUpProps): ApiResponse<SignUpResponse> =>
  api({ method: 'post', url: 'users', data });

export const socialSignUpService = (
  data: SocialSignUpProps
): ApiResponse<SignUpResponse> =>
  api({ method: 'post', url: 'social-auth/sign-up', data });

export const signOutService = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
};

interface MeResponse {
  success: boolean;
  data: User;
}

// Usuário logado com o plano atual — usado para re-hidratar a sessão (pega
// mudanças feitas fora dela, ex.: admin trocou o plano do cliente).
export const getMeService = (): ApiResponse<MeResponse> =>
  api({ method: 'get', url: 'users/me', useToken: true });

export const confirmEmailService = (
  data: ConfirmEmailProps
): ApiResponse<void> => api({ method: 'post', url: 'confirm-email', data });

export const confirmWhatsappNumberService = (
  data: ConfirmWhatsappProps
): ApiResponse<void> => api({ method: 'post', url: 'confirm-whatsapp', data });

export const forgotPasswordService = (
  data: ForgotPasswordProps
): ApiResponse<void> => api({ method: 'post', url: 'forgot-password', data });

export const resetPasswordService = (
  data: ResetPasswordProps
): ApiResponse<void> => api({ method: 'post', url: 'reset-password', data });

interface UpdateUserResponse {
  success: boolean;
  data: User;
}

export const updateUserService = (
  data: UpdateUserProps
): ApiResponse<UpdateUserResponse> =>
  api({
    method: 'patch',
    url: 'users',
    data,
    useToken: true,
  });

interface UpdateUserImageResponse {
  success: boolean;
  data: User;
}

export const updateUserImageService = ({
  file,
}: UploadUserImageProps): ApiResponse<UpdateUserImageResponse> => {
  const data = new FormData();
  data.append('image', file);

  return api({
    method: 'patch',
    url: 'users/image',
    data,
    useToken: true,
    hasFile: true,
  });
};

interface UpdateUserPlanResponse {
  success: boolean;
  data: User;
}

export const updateUserPlanService = (
  planName: string
): ApiResponse<UpdateUserPlanResponse> =>
  api({
    method: 'patch',
    url: 'users/plan',
    data: { planName },
    useToken: true,
  });

interface SpoofedUserService {
  success: boolean;
  data: {
    user: User;
    token: string;
  };
}

export const spoofedUserInService = (
  data: SpoofedInUserProps
): ApiResponse<SpoofedUserService> =>
  api({ method: 'POST', url: 'spoofed-sessions/in', data });

// Volta para a conta do admin sem relogar — o backend lê o adminId do próprio
// token impersonado, por isso não há body.
export const spoofedUserOutService = (): ApiResponse<SpoofedUserService> =>
  api({ method: 'POST', url: 'spoofed-sessions/out', useToken: true });

// ─── Prévia de cobrança (fonte única: backend) ──────────────────────────────

export interface PlanEstimate {
  base: number;
  additionalGroupA: { count: number; cost: number };
  additionalGroupB: { count: number; cost: number };
  total: number;
}

export interface BillingPreviewPlan {
  name: string;
  price: number;
  includedConsumerUnits: number;
  maxConsumerUnits: number | null; // null = ilimitado
  allowsGroupA: boolean;
  additionalCostGroupB: number;
  additionalCostGroupA: number;
  estimate: PlanEstimate;
}

export interface BillingPreview {
  currentPlan: string | null;
  usage: {
    groupACount: number;
    groupBCount: number;
    hiddenByPlanCount: number;
  };
  plans: BillingPreviewPlan[];
  // true → upgrades pagos exigem contato comercial (cobrança manual, sem gateway)
  paymentsEnforced: boolean;
}

interface BillingPreviewResponse {
  success: boolean;
  data: BillingPreview;
}

export const getBillingPreviewService =
  (): ApiResponse<BillingPreviewResponse> =>
    api({ method: 'get', url: 'users/billing-preview', useToken: true });

interface PlanContractRequestResponse {
  success: boolean;
  data: { planName: string; estimatedMonthly: number };
}

/**
 * "Quero contratar" (/conta) — dispara um e-mail para o comercial
 * (contato@energypass.com.br) com cópia para o próprio usuário. Não altera o
 * plano: quem ativa é o admin, depois de confirmar o pagamento.
 */
export const requestPlanContractService = (
  planName: string
): ApiResponse<PlanContractRequestResponse> =>
  api({
    method: 'post',
    url: 'users/plan-contract-request',
    data: { planName },
    useToken: true,
  });

/**
 * Aviso "geração automática agora é dos planos pagos" visto (out/2026): aparece
 * uma vez no login para a conta Free que já tinha inversor conectado.
 */
export const markMonitoringDowngradeNoticeSeenService = (): ApiResponse<{
  success: boolean;
}> =>
  api({
    method: 'post',
    url: 'users/monitoring-downgrade-notice',
    useToken: true,
  });
